import type { LatLng, RoundAnswer, RoundScore, SessionTotals } from "../types";

export type ShareCardEntry = {
  guess: LatLng | null;
  answer: LatLng;
  score: RoundScore;
  round: RoundAnswer;
};

type RenderArgs = {
  title: string;
  subtitle: string;
  totals: SessionTotals;
  entries: ShareCardEntry[];
  emoji: string;
};

export async function renderShareCard(args: RenderArgs): Promise<Blob> {
  const width = 1200;
  const height = 630;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context unavailable");
  }

  // Background gradient
  const bg = ctx.createLinearGradient(0, 0, width, height);
  bg.addColorStop(0, "#0f172a");
  bg.addColorStop(1, "#020617");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);

  // Subtle grid lines
  ctx.strokeStyle = "rgba(125, 211, 252, 0.10)";
  ctx.lineWidth = 1;
  for (let x = 0; x < width; x += 80) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  for (let y = 0; y < height; y += 80) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  drawWorldMap(ctx, 60, 110, 1080, 360, args.entries);

  // Title
  ctx.fillStyle = "#f8fafc";
  ctx.font = "700 56px 'Inter', system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(args.title, 60, 80);

  ctx.font = "500 22px 'Inter', system-ui, sans-serif";
  ctx.fillStyle = "rgba(248,250,252,0.62)";
  ctx.fillText(args.subtitle, 60, 110);

  // Score block
  const scoreX = width - 60;
  ctx.textAlign = "right";
  ctx.fillStyle = "#7dd3fc";
  ctx.font = "800 96px 'Inter', system-ui, sans-serif";
  ctx.fillText(args.totals.totalScore.toLocaleString(), scoreX, 130);

  ctx.font = "500 22px 'Inter', system-ui, sans-serif";
  ctx.fillStyle = "rgba(248,250,252,0.62)";
  ctx.fillText(`distance ${formatDistance(args.totals.totalDistanceKm)}  ·  off by ${args.totals.totalYearsOff} yrs`, scoreX, 160);

  // Emoji grid
  ctx.textAlign = "left";
  ctx.fillStyle = "#f8fafc";
  ctx.font = "600 36px 'Apple Color Emoji', 'Segoe UI Emoji', 'Inter', sans-serif";
  const emojiLines = args.emoji.split("\n").slice(1, 1 + args.entries.length);
  emojiLines.forEach((line, index) => {
    ctx.fillText(line, 60, 540 + index * 12);
  });

  // Footer
  ctx.fillStyle = "rgba(248,250,252,0.5)";
  ctx.font = "500 22px 'Inter', system-ui, sans-serif";
  ctx.textAlign = "right";
  ctx.fillText("LatLongAgo · play at /daily", width - 60, height - 40);

  return await canvasToBlob(canvas);
}

function drawWorldMap(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  entries: ShareCardEntry[]
): void {
  // Map background
  ctx.fillStyle = "rgba(15,23,42,0.7)";
  ctx.fillRect(x, y, width, height);

  // Latitude/longitude grid
  ctx.strokeStyle = "rgba(125,211,252,0.20)";
  ctx.lineWidth = 1;
  for (let lng = -180; lng <= 180; lng += 30) {
    const px = x + ((lng + 180) / 360) * width;
    ctx.beginPath();
    ctx.moveTo(px, y);
    ctx.lineTo(px, y + height);
    ctx.stroke();
  }

  for (let lat = -90; lat <= 90; lat += 30) {
    const py = y + ((90 - lat) / 180) * height;
    ctx.beginPath();
    ctx.moveTo(x, py);
    ctx.lineTo(x + width, py);
    ctx.stroke();
  }

  // Continents (very rough rectangles for visual anchoring)
  ctx.fillStyle = "rgba(56,189,248,0.10)";
  const continents: Array<[number, number, number, number]> = [
    [-170, 13, -52, 84], // North America
    [-90, -60, -34, 13], // South America
    [-25, 35, 45, 72], // Europe
    [-20, -36, 52, 38], // Africa
    [25, -12, 180, 82], // Asia
    [110, -50, 180, 0], // Oceania
    [-180, -90, 180, -60] // Antarctica
  ];
  for (const [minLng, minLat, maxLng, maxLat] of continents) {
    const px = x + ((minLng + 180) / 360) * width;
    const py = y + ((90 - maxLat) / 180) * height;
    const pw = ((maxLng - minLng) / 360) * width;
    const ph = ((maxLat - minLat) / 180) * height;
    ctx.fillRect(px, py, pw, ph);
  }

  // Pins
  for (const entry of entries) {
    const answerX = x + ((entry.answer.lng + 180) / 360) * width;
    const answerY = y + ((90 - entry.answer.lat) / 180) * height;

    if (entry.guess) {
      const guessX = x + ((entry.guess.lng + 180) / 360) * width;
      const guessY = y + ((90 - entry.guess.lat) / 180) * height;
      ctx.strokeStyle = "rgba(248,250,252,0.55)";
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(guessX, guessY);
      ctx.lineTo(answerX, answerY);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = "#7dd3fc";
      ctx.beginPath();
      ctx.arc(guessX, guessY, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = "#fb923c";
    ctx.beginPath();
    ctx.arc(answerX, answerY, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}

function formatDistance(km: number): string {
  if (!Number.isFinite(km)) return "-";
  const miles = km * 0.621371;
  if (miles < 0.1) return "<0.1 mi";
  return miles >= 100 ? `${Math.round(miles).toLocaleString()} mi` : `${miles.toFixed(1)} mi`;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Could not produce share card image."));
      }
    }, "image/png");
  });
}
